import {
  cloneElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
  ReactElement,
  RefObject,
} from 'react';
import { cn } from './cn';
import { Portal } from './Portal';
import { computePlacement } from './positioning';
import type { Placement, PopoverAlign, PopoverSide } from './positioning';

export interface TooltipProps {
  /** Tooltip text. Plain string by design — tooltips are never rich content. */
  label: string;
  /** Exactly one element. It receives `aria-describedby` while the tip is open. */
  children: ReactElement<{ 'aria-describedby'?: string }>;
  side?: PopoverSide;
  align?: PopoverAlign;
  /** Hover delay in ms. Keyboard focus always opens immediately. */
  delay?: number;
  disabled?: boolean;
}

interface BubbleProps {
  id: string;
  label: string;
  anchorRef: RefObject<HTMLSpanElement | null>;
  side: PopoverSide;
  align: PopoverAlign;
}

function TooltipBubble({ id, label, anchorRef, side, align }: BubbleProps): ReactElement {
  const ref = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const bubble = ref.current;
    if (!anchor || !bubble) return;
    setPlacement(
      computePlacement(
        anchor.getBoundingClientRect(),
        { width: bubble.offsetWidth, height: bubble.offsetHeight },
        { side, align, gap: 8, padding: 8 },
      ),
    );
  }, [anchorRef, side, align, label]);

  return (
    <div
      ref={ref}
      id={id}
      role="tooltip"
      style={{ top: placement?.top ?? 0, left: placement?.left ?? 0 }}
      className={cn(
        'pointer-events-none fixed z-[70] max-w-64 rounded-md border border-border',
        'bg-surface-raised px-2 py-1 text-xs leading-4 text-ink shadow-pop',
        'transition-opacity duration-100 motion-reduce:transition-none',
        placement ? 'opacity-100' : 'opacity-0',
      )}
    >
      {label}
    </div>
  );
}

/**
 * Hover *and* focus triggered tooltip.
 *
 * The wrapper span carries the listeners rather than the child, so a control
 * rendered with the native `disabled` attribute — which swallows its own
 * pointer events — still gets a tip.
 */
export function Tooltip({
  label,
  children,
  side = 'top',
  align = 'center',
  delay = 250,
  disabled = false,
}: TooltipProps): ReactElement {
  const id = useId();
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const timerRef = useRef<number | null>(null);
  // Focus that arrives from a click should not re-open what the click closed.
  const pointerFocusRef = useRef(false);
  const [open, setOpen] = useState(false);

  const cancelTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const close = useCallback(() => {
    cancelTimer();
    setOpen(false);
  }, [cancelTimer]);

  const openNow = useCallback(() => {
    cancelTimer();
    setOpen(true);
  }, [cancelTimer]);

  const openAfterDelay = useCallback(() => {
    cancelTimer();
    timerRef.current = window.setTimeout(() => setOpen(true), delay);
  }, [cancelTimer, delay]);

  useEffect(() => cancelTimer, [cancelTimer]);

  useEffect(() => {
    if (!open) return;
    // Covers the hover case, where focus lives somewhere else entirely.
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') close();
    }
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open, close]);

  const visible = open && !disabled && label.length > 0;
  const existing = children.props['aria-describedby'];
  const describedBy = visible ? (existing ? `${existing} ${id}` : id) : existing;

  function handlePointerEnter(event: ReactPointerEvent<HTMLSpanElement>): void {
    // Touch already has no hover state; showing on tap would fight the tap.
    if (event.pointerType === 'touch') return;
    openAfterDelay();
  }

  function handlePointerDown(): void {
    pointerFocusRef.current = true;
    close();
  }

  function handleFocus(): void {
    if (pointerFocusRef.current) {
      pointerFocusRef.current = false;
      return;
    }
    openNow();
  }

  function handleBlur(): void {
    pointerFocusRef.current = false;
    close();
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLSpanElement>): void {
    if (event.key !== 'Escape' || !open) return;
    event.stopPropagation();
    close();
  }

  return (
    <span
      ref={wrapperRef}
      className="inline-flex"
      onPointerEnter={handlePointerEnter}
      onPointerLeave={close}
      onPointerDown={handlePointerDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    >
      {cloneElement(children, { 'aria-describedby': describedBy })}
      {visible && (
        <Portal>
          <TooltipBubble id={id} label={label} anchorRef={wrapperRef} side={side} align={align} />
        </Portal>
      )}
    </span>
  );
}
